import { useMemo, useState } from 'react';
import { Flex, Image, Input, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { SearchImageRow } from '../../lib/ops-types';

export function filterSearchImages(rows: SearchImageRow[], f: { text: string; path: string | null; problemsOnly: boolean }) {
  const t = f.text.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!f.path || r.path === f.path) &&
      (!f.problemsOnly || r.problems.length > 0) &&
      (!t || r.displayName.toLowerCase().includes(t) || r.tags.some((tag) => tag.toLowerCase().includes(t)) || r.docId.toLowerCase().includes(t)),
  );
}

export function SearchImagesTab({ rows }: { rows: SearchImageRow[] }) {
  const [text, setText] = useState('');
  const [path, setPath] = useState<string | null>(null);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const paths = useMemo(() => [...new Set(rows.map((r) => r.path).filter((p): p is string => !!p))].sort(), [rows]);
  const data = useMemo(() => filterSearchImages(rows, { text, path, problemsOnly }), [rows, text, path, problemsOnly]);

  return (
    <>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 16 }}>
        <Input.Search allowClear placeholder="표시명·태그·문서 ID" style={{ width: 260 }} onChange={(e) => setText(e.target.value)} />
        <Select allowClear placeholder="경로(path)" style={{ width: 180 }} value={path} onChange={(v) => setPath(v ?? null)} options={paths.map((p) => ({ value: p, label: p }))} />
        <Flex gap={8} align="center">
          <Switch size="small" checked={problemsOnly} onChange={setProblemsOnly} aria-label="점검 필요만" />
          <Typography.Text>점검 필요만</Typography.Text>
        </Flex>
        <Typography.Text type="secondary">{data.length} / {rows.length}건</Typography.Text>
      </Flex>
      <Typography.Paragraph type="secondary">
        이 목록은 태그 데이터 확인용입니다. 실제 앱 검색 결과(번역·Pixabay 포함)는 &lsquo;검색 결과 확인&rsquo; 화면에서 확인합니다.
      </Typography.Paragraph>
      <Table
        size="small"
        rowKey="docId"
        dataSource={data}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: 900 }}
        columns={[
          { title: '이미지', dataIndex: 'imageUrl', width: 72, render: (u: string | null, r) => (u ? <Image src={u} width={48} height={48} style={{ objectFit: 'contain' }} alt={r.displayName} /> : '-') },
          { title: '표시명', dataIndex: 'displayName', width: 140 },
          { title: '태그', dataIndex: 'tags', render: (tags: string[]) => <Flex wrap gap={4}>{tags.map((t) => <Tag key={t}>{t}</Tag>)}</Flex> },
          { title: '경로', dataIndex: 'path', width: 120, render: (p: string | null) => (p ? <Typography.Text code>{p}</Typography.Text> : '-') },
          { title: '문서 ID', dataIndex: 'docId', width: 200, render: (v: string) => <Typography.Text copyable type="secondary">{v}</Typography.Text> },
          {
            title: '점검',
            dataIndex: 'problems',
            width: 80,
            render: (p: string[]) =>
              p.length ? (
                <Tooltip title={<ul style={{ margin: 0, paddingLeft: 16 }}>{p.map((x) => <li key={x}>{x}</li>)}</ul>}>
                  <Tag color="orange" icon={<WarningOutlined />}>{p.length}</Tag>
                </Tooltip>
              ) : (
                <Tag color="green">정상</Tag>
              ),
          },
        ]}
      />
    </>
  );
}
